import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Calendar, TrendingUp, Store as StoreIcon, Download } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { statsApi } from '../api/stats';
import { receiptsApi } from '../api/receipts';
import type { MonthlyDataItem, CategorySpendingItem, StoreSpendingItem } from '../api/stats';
import type { Receipt as ReceiptType, PaginatedResponse } from '../types';
import { toFixed, formatEUR } from '../utils/format';
import { Button } from '../components/ui/Button';

const CHART_COLORS = [
  '#3B82F6', '#8B5CF6', '#F59E0B', '#22C55E', '#EF4444',
  '#06B6D4', '#EC4899', '#14B8A6', '#F97316', '#6366F1',
];

export const StatsPage: React.FC = () => {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);

  const handleCsvExport = async () => {
    setExporting(true);
    try {
      // Fetch all receipts (large page size to get everything)
      const allReceipts: ReceiptType[] = [];
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const res: PaginatedResponse<ReceiptType> = await receiptsApi.list({ page, page_size: 100 });
        allReceipts.push(...res.items);
        hasMore = page < res.pages;
        page++;
      }

      // Build CSV
      const header = 'Datum,Geschaeft,Betrag,Kategorie';
      const rows = allReceipts.map((r) => {
        const datum = r.receipt_date
          ? new Date(r.receipt_date).toLocaleDateString('de-DE')
          : '';
        const store = (r.store?.name || r.store_name || '').replace(/,/g, ' ');
        const betrag = r.total_amount != null ? toFixed(r.total_amount, 2) : '0.00';
        const kategorie = (r.items?.[0]?.category?.name || '').replace(/,/g, ' ');
        return `${datum},${store},${betrag},${kategorie}`;
      });
      const csv = [header, ...rows].join('\n');

      // Trigger download
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `kassenzettel-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      // silently fail — user sees no download
    } finally {
      setExporting(false);
    }
  };

  const dateFilters = {
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  };

  const { data: monthly, isLoading: loadingMonthly, isError: errorMonthly } = useQuery<MonthlyDataItem[]>({
    queryKey: ['stats-monthly', dateFrom, dateTo],
    queryFn: () => statsApi.getMonthlyTrend(12, dateFilters),
  });

  const { data: categories, isLoading: loadingCategories, isError: errorCategories } = useQuery<CategorySpendingItem[]>({
    queryKey: ['stats-categories', dateFrom, dateTo],
    queryFn: () => statsApi.getByCategory(dateFilters),
  });

  const { data: topStores, isLoading: loadingStores, isError: errorStores } = useQuery<StoreSpendingItem[]>({
    queryKey: ['stats-stores', dateFrom, dateTo],
    queryFn: () => statsApi.getByStore(dateFilters),
  });

  // BUG FIX: war && statt || — Spinner wurde nur gezeigt wenn ALLE laden
  const isLoading = loadingMonthly || loadingCategories || loadingStores;

  if (isLoading) {
    return <LoadingSpinner fullPage text="Statistiken werden geladen..." />;
  }

  if (errorMonthly || errorCategories || errorStores) {
    return <Card><p className="text-center py-8 text-error-500">Daten konnten nicht geladen werden. Bitte versuche es erneut.</p></Card>;
  }

  // BUG FIX: Number() wrapping für alle Backend-Werte (kommen als Strings via Decimal-Serialisierung)
  const MONTH_NAMES = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  const monthlyData = (monthly || []).map((m) => {
    const [y, mo] = String(m.month).split('-');
    return {
      month: `${MONTH_NAMES[parseInt(mo, 10) - 1] || mo} ${y.slice(2)}`,
      amount: Number(m.total) || 0,
      receipt_count: Number(m.receipt_count) || 0,
    };
  });

  const categoryData = (categories || []).map((c) => ({
    category_id: c.category_id,
    category_name: c.category_name,
    amount: Number(c.total) || 0,
    percentage: 0, // wird unten korrekt berechnet
    color: '',
  }));

  // Sortiert nach höchstem Betrag
  categoryData.sort((a, b) => b.amount - a.amount);

  const totalCategoryAmount = categoryData.reduce((sum, c) => sum + c.amount, 0);

  // Prozente korrekt berechnen (muss 100% ergeben)
  categoryData.forEach((c) => {
    c.percentage = totalCategoryAmount > 0 ? (c.amount / totalCategoryAmount) * 100 : 0;
  });

  const storeData = (topStores || []).map((s) => ({
    store_id: s.store_id,
    store_name: s.store_name,
    total_amount: Number(s.total) || 0,
    visit_count: Number(s.receipt_count) || 0,
  }));

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-neutral-800 dark:text-white">Statistiken</h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">Analyse deiner Ausgaben</p>
        </div>

        {/* Date Filter + Export */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-neutral-400" />
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="px-3 py-1.5 text-sm border border-neutral-300 dark:border-indigo-500/20 rounded-lg
                         bg-white dark:bg-[#1e1e2e]/60 dark:text-white
                         focus:outline-none focus:ring-2 focus:ring-primary-500/20"
              placeholder="Von"
            />
          </div>
          <span className="text-neutral-400">-</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-1.5 text-sm border border-neutral-300 dark:border-indigo-500/20 rounded-lg
                       bg-white dark:bg-[#1e1e2e]/60 dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-primary-500/20"
            placeholder="Bis"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={handleCsvExport}
            loading={exporting}
          >
            <Download className="w-4 h-4 mr-1" />
            Als CSV exportieren
          </Button>
        </div>
      </div>

      {/* Monthly Comparison Chart — horizontal bars with inline amounts */}
      <Card padding="lg">
        <CardHeader>
          <CardTitle>Monatsvergleich</CardTitle>
          <div className="flex items-center gap-1 text-sm text-neutral-500 dark:text-neutral-400">
            <TrendingUp className="w-4 h-4" />
            Ausgaben pro Monat
          </div>
        </CardHeader>
        <div>
          {monthlyData.length > 0 ? (
            <div className="space-y-3">
              {monthlyData.map((m) => {
                const maxAmount = Math.max(...monthlyData.map(d => Number(d.amount) || 0), 1);
                const amount = Number(m.amount) || 0;
                const pct = Math.round((amount / maxAmount) * 100);
                return (
                  <div key={m.month}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400">{m.month}</span>
                      <span className="text-sm font-bold font-mono tabular-nums text-neutral-800 dark:text-white">{formatEUR(amount)}</span>
                    </div>
                    <div className="w-full bg-neutral-100 dark:bg-white/5 rounded-full h-7 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 flex items-center justify-end pr-2 transition-all duration-500"
                        style={{ width: `${Math.max(pct, 8)}%` }}
                      >
                        {pct > 25 && (
                          <span className="text-xs font-bold text-white/90 tabular-nums">{formatEUR(amount)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center py-10 text-neutral-400 text-sm">
              Noch keine Daten vorhanden
            </div>
          )}
        </div>
      </Card>

      {/* Category + Stores Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Category Distribution — Horizontal Bar Chart */}
        <Card padding="lg">
          <CardHeader>
            <CardTitle>Kategorie-Verteilung</CardTitle>
            {totalCategoryAmount > 0 && (
              <span className="text-sm text-neutral-500 dark:text-neutral-400">
                Gesamt: {formatEUR(totalCategoryAmount)}
              </span>
            )}
          </CardHeader>
          {categoryData.length > 0 ? (
            <div className="space-y-4 mt-2">
              {categoryData.map((cat, index) => (
                <div key={cat.category_id} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                      />
                      <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                        {cat.category_name}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-mono font-semibold tabular-nums text-neutral-800 dark:text-neutral-100">
                        {formatEUR(cat.amount)}
                      </span>
                      <span className="text-xs text-neutral-400 w-12 text-right">
                        {cat.percentage.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                  <div className="w-full h-3 bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${cat.percentage}%`,
                        backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 text-neutral-400 text-sm">
              Noch keine Daten vorhanden
            </div>
          )}
        </Card>

        {/* Top Stores */}
        <Card padding="lg">
          <CardHeader>
            <CardTitle>Top Geschäfte</CardTitle>
          </CardHeader>
          {storeData.length > 0 ? (
            <div className="space-y-4">
              {storeData.map((store, index) => {
                const maxAmount = storeData[0]?.total_amount || 1;
                const percentage = (store.total_amount / maxAmount) * 100;

                return (
                  <div key={store.store_id} className="space-y-1.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-neutral-400 w-5">
                          {index + 1}.
                        </span>
                        <StoreIcon className="w-4 h-4 text-neutral-400" />
                        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                          {store.store_name}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-mono font-semibold tabular-nums text-neutral-800 dark:text-neutral-100">
                          {formatEUR(store.total_amount)}
                        </span>
                        <span className="text-xs text-neutral-400 ml-2">
                          ({store.visit_count}x)
                        </span>
                      </div>
                    </div>
                    <div className="ml-7 h-2 bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex items-center justify-center h-48 text-neutral-400 text-sm">
              Noch keine Daten vorhanden
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
